// MediaPipe gesture recognition (lazy-loaded for code-splitting)
import * as THREE from 'three';

// These are dynamically imported for code-splitting
let mpVision = false;
let mpHandLandmarker: any = null;
let mpFaceLandmarker: any = null;
let mediaPipeLoading = false;

export interface GestureState {
  handPos: { x: number; z: number };
  headPos: { x: number; y: number };
  handOpen: number;
  thumbExt: number;
  indexExt: number;
  middleExt: number;
  ringExt: number;
  pinkyExt: number;
  headTilt: { x: number; y: number };
}

export const gestureState: GestureState = {
  handPos: { x: 0, z: 0 },
  headPos: { x: 0, y: 0 },
  handOpen: 1.0,
  thumbExt: 1.0,
  indexExt: 1.0,
  middleExt: 1.0,
  ringExt: 1.0,
  pinkyExt: 1.0,
  headTilt: { x: 0, y: 0 }
};

let webcamVideo: HTMLVideoElement | null = null;
let webcamStream: MediaStream | null = null;
let lastWebcamTime = -1;

export async function startWebcam(): Promise<boolean> {
  try {
    webcamVideo = document.getElementById('webcam') as HTMLVideoElement;
    if (!webcamStream || webcamStream.getTracks().every(t => t.readyState === 'ended')) {
      webcamStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, frameRate: 30 }
      });
    }
    webcamVideo.srcObject = webcamStream;
    const preview = document.getElementById('webcamPreview');
    if (preview) preview.style.display = 'block';

    return new Promise((resolve) => {
      if (webcamVideo!.readyState >= 2) {
        webcamVideo!.play();
        resolve(true);
        return;
      }
      webcamVideo!.onloadedmetadata = () => {
        webcamVideo!.play();
        resolve(true);
      };
    });
  } catch (e) {
    console.warn("Webcam access rejected:", (e as Error).message);
    return false;
  }
}

export function stopWebcam() {
  if (webcamStream) {
    webcamStream.getTracks().forEach(track => track.stop());
    webcamStream = null;
  }
  if (webcamVideo) webcamVideo.srcObject = null;
  const preview = document.getElementById('webcamPreview');
  if (preview) preview.style.display = 'none';
}

export async function loadMediaPipe(): Promise<boolean> {
  if (mpVision) return true;
  if (mediaPipeLoading) return false;
  mediaPipeLoading = true;

  try {
    const vision = await import('https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.3/+esm');
    const { FilesetResolver, HandLandmarker, FaceLandmarker } = vision;

    const wasmFileset = await FilesetResolver.forVisionTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.3/wasm"
    );

    mpHandLandmarker = await HandLandmarker.createFromOptions(wasmFileset, {
      baseOptions: {
        modelAssetPath: "https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task",
        delegate: "GPU"
      },
      runningMode: "VIDEO",
      numHands: 1
    });

    mpFaceLandmarker = await FaceLandmarker.createFromOptions(wasmFileset, {
      baseOptions: {
        modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
        delegate: "GPU"
      },
      runningMode: "VIDEO",
      numFaces: 1
    });

    mpVision = true;
    mediaPipeLoading = false;
    return true;
  } catch (err) {
    mediaPipeLoading = false;
    console.error("MediaPipe failed to load:", err);
    return false;
  }
}

export function runGestureTracking(
  timestamp: number,
  gestureControl: 'Off' | 'Hand' | 'Head',
  EXT: number,
  onFingerRipple?: (x: number, z: number, color: { r: number; g: number; b: number }) => void
) {
  if (!webcamVideo || webcamVideo.readyState < 2 || !mpVision) return;
  if (webcamVideo.currentTime === lastWebcamTime) return;
  lastWebcamTime = webcamVideo.currentTime;

  if (gestureControl === 'Hand' && mpHandLandmarker) {
    const results = mpHandLandmarker.detectForVideo(webcamVideo, timestamp);
    if (results.landmarks && results.landmarks.length > 0) {
      const l = results.landmarks[0];

      const rawX = 1.0 - l[9].x;
      const rawY = l[9].y;
      gestureState.handPos.x = (rawX - 0.5) * EXT;
      gestureState.handPos.z = (rawY - 0.5) * EXT;

      const wrist = l[0];
      const handSize = Math.hypot(l[9].x - wrist.x, l[9].y - wrist.y) || 1.0;

      gestureState.thumbExt = THREE.MathUtils.clamp(
        (Math.hypot(l[4].x - wrist.x, l[4].y - wrist.y) / handSize - 0.6) / 0.8, 0.0, 1.0
      );
      gestureState.indexExt = THREE.MathUtils.clamp(
        (Math.hypot(l[8].x - wrist.x, l[8].y - wrist.y) / handSize - 0.8) / 1.1, 0.0, 1.0
      );
      gestureState.middleExt = THREE.MathUtils.clamp(
        (Math.hypot(l[12].x - wrist.x, l[12].y - wrist.y) / handSize - 0.9) / 1.2, 0.0, 1.0
      );
      gestureState.ringExt = THREE.MathUtils.clamp(
        (Math.hypot(l[16].x - wrist.x, l[16].y - wrist.y) / handSize - 0.85) / 1.1, 0.0, 1.0
      );
      gestureState.pinkyExt = THREE.MathUtils.clamp(
        (Math.hypot(l[20].x - wrist.x, l[20].y - wrist.y) / handSize - 0.7) / 1.0, 0.0, 1.0
      );

      gestureState.handOpen = (
        gestureState.thumbExt + gestureState.indexExt + gestureState.middleExt +
        gestureState.ringExt + gestureState.pinkyExt
      ) / 5.0;

      // Finger ripple detection
      if (onFingerRipple) {
        const fingersData = [
          { tipIdx: 4, ext: gestureState.thumbExt, color: { r: 1.0, g: 0.2, b: 0.2 } },
          { tipIdx: 8, ext: gestureState.indexExt, color: { r: 0.0, g: 0.9, b: 1.0 } },
          { tipIdx: 12, ext: gestureState.middleExt, color: { r: 0.0, g: 1.0, b: 0.5 } },
          { tipIdx: 16, ext: gestureState.ringExt, color: { r: 0.8, g: 0.2, b: 1.0 } },
          { tipIdx: 20, ext: gestureState.pinkyExt, color: { r: 1.0, g: 0.75, b: 0.0 } }
        ];

        fingersData.forEach(f => {
          const tipL = l[f.tipIdx];
          const tipX = (1.0 - tipL.x - 0.5) * EXT;
          const tipZ = (tipL.y - 0.5) * EXT;
          if (f.ext > 0.75) {
            onFingerRipple(tipX, tipZ, f.color);
          }
        });
      }
    } else {
      // Hand lost - reset
      gestureState.handPos = { x: 0, z: 0 };
      gestureState.handOpen = 1.0;
      gestureState.thumbExt = 1.0;
      gestureState.indexExt = 1.0;
      gestureState.middleExt = 1.0;
      gestureState.ringExt = 1.0;
      gestureState.pinkyExt = 1.0;
    }
  } else if (gestureControl === 'Head' && mpFaceLandmarker) {
    const results = mpFaceLandmarker.detectForVideo(webcamVideo, timestamp);
    if (results.faceLandmarks && results.faceLandmarks.length > 0) {
      const landmarks = results.faceLandmarks[0];
      const noseTip = landmarks[4] || landmarks[1] || landmarks[0];

      gestureState.headPos.x = THREE.MathUtils.lerp(gestureState.headPos.x, (0.5 - noseTip.x) * 2.2, 0.1);
      gestureState.headPos.y = THREE.MathUtils.lerp(gestureState.headPos.y, (noseTip.y - 0.5) * 2.2, 0.1);
      gestureState.headTilt.x = gestureState.headPos.x * 0.40;
      gestureState.headTilt.y = gestureState.headPos.y * 0.30;
    } else {
      gestureState.headPos.x = THREE.MathUtils.lerp(gestureState.headPos.x, 0, 0.08);
      gestureState.headPos.y = THREE.MathUtils.lerp(gestureState.headPos.y, 0, 0.08);
      gestureState.headTilt.x = THREE.MathUtils.lerp(gestureState.headTilt.x, 0, 0.08);
      gestureState.headTilt.y = THREE.MathUtils.lerp(gestureState.headTilt.y, 0, 0.08);
    }
  } else {
    // Disabled - reset all
    gestureState.handPos = { x: 0, z: 0 };
    gestureState.handOpen = 1.0;
    gestureState.thumbExt = 1.0;
    gestureState.indexExt = 1.0;
    gestureState.middleExt = 1.0;
    gestureState.ringExt = 1.0;
    gestureState.pinkyExt = 1.0;
    gestureState.headTilt = { x: 0, y: 0 };
  }
}

export function resetGestureState() {
  gestureState.handPos = { x: 0, z: 0 };
  gestureState.headPos = { x: 0, y: 0 };
  gestureState.handOpen = 1.0;
  gestureState.thumbExt = 1.0;
  gestureState.indexExt = 1.0;
  gestureState.middleExt = 1.0;
  gestureState.ringExt = 1.0;
  gestureState.pinkyExt = 1.0;
  gestureState.headTilt = { x: 0, y: 0 };
}